import { DateTime, Duration } from 'luxon'
import { useState } from 'react'

import { useAppContext } from '@/client/context/app'
import useInterval from '@/client/hooks/useInterval'

import useRealtimeSessionJoinInfo from '@/components/Worlds/hooks/session/useRealtimeSessionJoinInfo'

import {
  NO_SHOW_THRESHOLD_MINUTES,
  NO_SHOW_WARN_THRESHOLD_MINUTES,
  SOON_THRESHOLD_MINUTES,
  ZOOM_OPEN_THRESHOLD_MINUTES,
} from './constants'
import { type BannerState, BannerStateType, type UseBannerStateProps } from './types'

// This computation is pretty lightweight, update every second to be as accurate as possible
const REFRESH_INTERVAL_SECONDS = 1

function getBannerState({
  startDateTime,
  endDateTime,
  zoomEndDateTime,
  attemptedHosting,
  now,
}: UseBannerStateProps & { now: DateTime }): BannerState {
  // In dry-run, if startDateTime or endDateTime is something like
  // DateTime.fromISO(session(Session.start_date) ?? ''), then the datetime,
  // DateTime.fromISO(''), will be invalid and we'll get an error trying to
  // diff invalid datetimes.
  const startFromNow = startDateTime.isValid ? startDateTime.diff(now) : Duration.fromMillis(0)
  const endFromNow = endDateTime.isValid ? endDateTime.diff(now) : Duration.fromMillis(0)

  const minutesUntilStart = startFromNow.as('minutes')
  const minutesUntilEnd = endFromNow.as('minutes')

  if (!startDateTime.isValid || !endDateTime.isValid) {
    return {
      type: BannerStateType.None,
      startFromNow,
    }
  }

  if (minutesUntilEnd < 0) {
    return {
      type: BannerStateType.None,
      startFromNow,
    }
  }

  if (minutesUntilStart > SOON_THRESHOLD_MINUTES) {
    return {
      type: BannerStateType.UpcomingDistant,
      startFromNow,
    }
  }

  if (minutesUntilStart > ZOOM_OPEN_THRESHOLD_MINUTES) {
    return {
      type: BannerStateType.UpcomingSoonZoomClosed,
      startFromNow,
    }
  }

  if (zoomEndDateTime != null && zoomEndDateTime < now) {
    return {
      type: BannerStateType.LiveSessionEndedEarly,
      startFromNow,
    }
  }

  if (minutesUntilStart > 0) {
    return {
      type: BannerStateType.UpcomingSoonZoomOpen,
      startFromNow,
    }
  }

  if (minutesUntilStart > NO_SHOW_WARN_THRESHOLD_MINUTES) {
    return {
      type: BannerStateType.Live,
      startFromNow,
    }
  }

  if (!attemptedHosting) {
    if (
      minutesUntilStart < NO_SHOW_WARN_THRESHOLD_MINUTES &&
      minutesUntilStart > NO_SHOW_THRESHOLD_MINUTES
    ) {
      return {
        type: BannerStateType.LiveNoShowWarning,
        startFromNow,
      }
    }
    return {
      type: BannerStateType.NoShow,
      startFromNow,
    }
  }

  return {
    type: BannerStateType.Live,
    startFromNow,
  }
}

export default function useBannerState({
  sessionId,
  startDateTime,
  endDateTime,
  zoomEndDateTime,
  attemptedHosting,
}: UseBannerStateProps & { sessionId: number }): BannerState {
  const { session: realtimeSession } = useRealtimeSessionJoinInfo(sessionId)
  const { requestTimestamp } = useAppContext()

  const [bannerState, setBannerState] = useState(() =>
    getBannerState({
      startDateTime,
      endDateTime,
      zoomEndDateTime,
      attemptedHosting,
      now: DateTime.fromISO(requestTimestamp),
    })
  )

  // Tick the button state every [REFRESH_INTERVAL_SECONDS] seconds
  useInterval(
    () => {
      setBannerState(
        getBannerState({
          startDateTime: realtimeSession == null ? startDateTime : realtimeSession.startDate,
          endDateTime: realtimeSession == null ? endDateTime : realtimeSession.endDate,
          zoomEndDateTime: realtimeSession == null ? zoomEndDateTime : realtimeSession.zoomEndDate,
          attemptedHosting:
            realtimeSession == null ? attemptedHosting : realtimeSession.attemptedHosting,
          // DateTime.now() is correct here (not server time) because this runs client-side
          // on an interval after the page has loaded, so we want the live local clock.
          now: DateTime.now(),
        })
      )
    },
    REFRESH_INTERVAL_SECONDS * 1000,
    [startDateTime, endDateTime, realtimeSession]
  )

  return { ...bannerState }
}
